import { Button as AntButton, Flex, theme } from 'antd';
import type { GlobalToken } from 'antd';
import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { radius } from '../../theme/radius';

export type PillToggleOption<T extends string> = {
  value: T;
  label: ReactNode;
};

export type PillToggleGroupProps<T extends string> = {
  options: readonly PillToggleOption<T>[];
  /** The picked values. */
  value: readonly T[];
  /** The new picks, in the options' order. */
  onChange: (value: T[]) => void;
  /** Keep at least one pick: the last picked pill can't be unpicked. */
  required?: boolean;
  /** Let the pills wrap onto more lines on a narrow container. */
  wrap?: boolean;
  'aria-label'?: string;
  className?: string;
};

/**
 * PillToggleGroup — a multi-select row of pill toggles. Looks like a pill
 * `Segmented`, but any number of pills can be picked; each is a button with
 * `aria-pressed`.
 *
 * @example
 * ```tsx
 * <PillToggleGroup
 *   required
 *   options={[{ value: 'left', label: t('left') }, { value: 'right', label: t('right') }]}
 *   value={sides}
 *   onChange={setSides}
 * />
 * ```
 */
export const PillToggleGroup = <T extends string>({
  options,
  value,
  onChange,
  required = false,
  wrap = false,
  'aria-label': ariaLabel,
  className,
}: PillToggleGroupProps<T>) => {
  const { token } = theme.useToken();

  const toggle = (option: T) => {
    const picked = value.includes(option);
    if (picked && required && value.length === 1) return;
    const next = picked
      ? value.filter((v) => v !== option)
      : [...value, option];
    onChange(options.map((o) => o.value).filter((v) => next.includes(v)));
  };

  return (
    <StyledTrack
      role="group"
      aria-label={ariaLabel}
      wrap={wrap ? 'wrap' : undefined}
      gap={wrap ? 6 : 0}
      className={className}
      $token={token}
      $wrap={wrap}
    >
      {options.map((option) => {
        const picked = value.includes(option.value);
        const locked = picked && required && value.length === 1;
        return (
          <StyledPill
            key={option.value}
            type="text"
            shape="round"
            aria-pressed={picked}
            // Not `disabled`: the pill stays focusable and keeps its picked look.
            aria-disabled={locked || undefined}
            onClick={() => toggle(option.value)}
            $token={token}
            $picked={picked}
            $locked={locked}
          >
            {option.label}
          </StyledPill>
        );
      })}
    </StyledTrack>
  );
};

export default PillToggleGroup;

// ─── Styled components ───

const StyledTrack = styled(Flex)<{ $token: GlobalToken; $wrap: boolean }>`
  align-self: flex-start;
  max-width: 100%;
  padding: 4px;
  background: ${({ $token }) => $token.colorFillSecondary};
  border: 1px solid ${({ $token }) => $token.colorBorder};

  /* A wrapping track gets a fixed radius rather than a capsule. */
  border-radius: ${({ $wrap }) => ($wrap ? radius.box : radius.pill)}px;
`;

// Over antd's text button, which brings hover, focus ring and motion.
// `!important` beats antd's hover and active rules, as in `Button`.
const StyledPill = styled(AntButton)<{
  $token: GlobalToken;
  $picked: boolean;
  $locked: boolean;
}>`
  height: auto;
  padding: 7px 16px;
  font-weight: 500;
  line-height: normal;
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
  color: ${({ $token, $picked }) =>
    $picked ? $token.colorText : $token.colorTextLabel};
  background: ${({ $token, $picked }) =>
    $picked ? $token.colorBgElevated : 'transparent'};
  box-shadow: ${({ $token, $picked }) =>
    $picked ? $token.boxShadowTertiary : 'none'};

  ${({ $token, $picked }) =>
    $picked
      ? css`
          &:hover,
          &:active {
            color: ${$token.colorText} !important;
            background: ${$token.colorBgElevated} !important;
          }
        `
      : null}
`;
